import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigateTo, useRouteContextHeaders } from "@tecnobit-srl/router";
import axios from "axios";
import { filter, flatten, includes, isArray, isEmpty, isEqual, isString, join, map, omitBy, sortBy, split, trimEnd } from "lodash-es";
import { useMenuManagerContext } from "@tecnobit-srl/menu";
import { jsx } from "react/jsx-runtime";
//#region src/use-infinite-scroll.ts
function getWindowScroll() {
	return window.scrollY + window.innerHeight;
}
function useInfiniteScroll({ active, containerRef, offset = 300, onEndReached }) {
	const timeoutRef = useRef(false);
	const [windowScroll, setWindowScroll] = useState(getWindowScroll());
	useEffect(() => {
		const onScroll = () => {
			setWindowScroll(() => getWindowScroll());
		};
		if (active) window.addEventListener("scroll", onScroll);
		return () => {
			window.removeEventListener("scroll", onScroll);
		};
	}, [active]);
	useEffect(() => {
		if (active && containerRef?.current && windowScroll >= containerRef.current.offsetHeight + containerRef.current.offsetTop - offset && !timeoutRef.current) {
			timeoutRef.current = true;
			Promise.resolve(onEndReached()).catch((e) => {
				console.error(`Error on infinite scroll end reached callback:`, e);
			}).finally(() => {
				timeoutRef.current = false;
			});
		}
	}, [
		active,
		onEndReached,
		windowScroll,
		containerRef,
		offset
	]);
}
//#endregion
//#region src/api-pagination-url.ts
var buildApiPaginationUrl = ({ fetchUrl, filters, page, currentPageParam, perPage, perPageParam, stringifyFilterValue }) => {
	const [baseUrl, query = ""] = fetchUrl.split("?", 2);
	let url = baseUrl;
	const queryParams = new URLSearchParams(query);
	const values = {
		...filters,
		...page && page > 0 ? { [currentPageParam]: page } : {},
		...perPage > 0 ? { [perPageParam]: perPage } : {}
	};
	for (const key in values) {
		const value = values[key];
		const token = `:${key}:`;
		if (fetchUrl.includes(token)) {
			if (url.includes(token)) url = url.replace(token, stringifyFilterValue(key, value));
			queryParams.delete(key);
		} else if (value !== "" && (!Array.isArray(value) || value.length > 0)) {
			if (Array.isArray(value)) for (const item of value) queryParams.set(`${key}[]`, String(item));
			else queryParams.set(key, String(value));
		}
	}
	const queryString = queryParams.toString();
	return queryString ? `${url}?${queryString}` : url;
};
//#endregion
//#region src/use-pagination.tsx
var PaginationContext = createContext(void 0);
/**
* Keep at most `limit` values.
*
* Only lists of strings are capped. A `[from, to]` pair of numbers is a range, and cutting
* it would not reduce a selection, it would change what the filter means.
*/
var clampFilterValue = (value, limit) => {
	if (!isArray(value)) return value;
	const slugs = value.filter((v) => isString(v));
	return slugs.length === value.length && slugs.length > limit ? slugs.slice(0, limit) : value;
};
function PaginationProvider({ children, currentPage, currentPageParam = "page", perPage, perPageParam = "perPage", multiFilterSeparator = "_", total: initialTotal, prefetched = true, fetchUrl, filtersUrl = {}, infiniteScroll, scrollTopOffset = 200, reloadOnly, getDataFromApiResponse, getTotalFromApiResponse, onApiResponse: onApiResponseFromProps, initialData, initialFilters = {}, filterSelectionLimits, apiFetch = false, onNavigate }) {
	useEffect(() => {
		if (!fetchUrl && apiFetch) throw new Error("fetchUrl must be set when apiFetch is true");
		if (fetchUrl && !apiFetch) throw new Error("fetchUrl must NOT be set when apiFetch is false");
	}, [apiFetch, fetchUrl]);
	const navigateTo = useNavigateTo();
	const mergeRouteContextHeaders = useRouteContextHeaders();
	const getCurrentUrl = useCallback(() => {
		return `${window.location.pathname}${window.location.search}`;
	}, []);
	const [loading, setLoading] = useState(false);
	const initialFiltersRef = useRef(initialFilters);
	if (!isEqual(initialFiltersRef.current, initialFilters)) initialFiltersRef.current = initialFilters;
	const stableInitialFilters = initialFiltersRef.current;
	const [filters, setFilters] = useState(stableInitialFilters);
	useEffect(() => {
		setFilters(stableInitialFilters);
	}, [stableInitialFilters]);
	const [innerCurrentPage, setInnerCurrentPage] = useState(currentPage);
	useEffect(() => {
		if (prefetched) setInnerCurrentPage(currentPage);
	}, [currentPage, prefetched]);
	const [data, setData] = useState({ [currentPage]: initialData });
	useEffect(() => {
		if (prefetched) setData((d) => ({
			...d,
			[currentPage]: initialData
		}));
	}, [
		currentPage,
		initialData,
		prefetched
	]);
	const [total, setTotal] = useState(initialTotal ?? data[currentPage]?.length ?? 0);
	useEffect(() => {
		if (prefetched) setTotal(initialTotal ?? 0);
	}, [
		currentPage,
		initialTotal,
		prefetched
	]);
	useEffect(() => {
		if (!apiFetch) return;
		const st = window.history.state;
		const snapshot = {
			data,
			currentPage: innerCurrentPage,
			filters,
			total,
			url: getCurrentUrl()
		};
		window.history.replaceState({
			...st,
			__pagination: snapshot
		}, "", getCurrentUrl());
	}, [
		apiFetch,
		data,
		filters,
		getCurrentUrl,
		innerCurrentPage,
		total
	]);
	const pushToHistory = useCallback((nextUrl) => {
		const prevState = window.history.state && typeof window.history.state === "object" ? window.history.state : {};
		window.history.pushState({
			...prevState,
			__pagination: void 0
		}, "", nextUrl);
	}, []);
	useEffect(() => {
		if (!apiFetch) return;
		const onPop = (e) => {
			const snapshot = e.state?.__pagination;
			if (snapshot) {
				setLoading(true);
				setData(snapshot.data);
				setTotal(snapshot.total);
				setFilters(snapshot.filters);
				setInnerCurrentPage(snapshot.currentPage);
				setLoading(false);
			}
		};
		window.addEventListener("popstate", onPop);
		return () => {
			window.removeEventListener("popstate", onPop);
		};
	}, [apiFetch]);
	const stringifyFilterValue = useCallback((key, value) => {
		if (filtersUrl.__ranges__?.[key]) {
			const rangeObj = filtersUrl.__ranges__[key];
			if (isArray(value)) {
				const hasFrom = value.length > 1 && value[0] !== null && typeof value[0] === "number";
				const hasTo = value.length >= 2 && value[1] !== null && typeof value[1] === "number";
				if (hasFrom && hasTo) return rangeObj.from_to.replace("[FROM]", value[0].toString()).replace("[TO]", value[1].toString());
				else if (hasFrom) return rangeObj.from.replace("[FROM]", value[0].toString());
				else if (hasTo) return rangeObj.to.replace("[TO]", value[1].toString());
				return "";
			}
			return rangeObj.from.replace("[FROM]", value.toString());
		}
		if (isArray(value)) return join(map(value, (v) => String(v)), multiFilterSeparator);
		return String(value);
	}, [filtersUrl.__ranges__, multiFilterSeparator]);
	const getFrontendUrl = useCallback((newFilters, page) => {
		const f = {
			...newFilters,
			...page && page > 1 ? { [currentPageParam]: page } : {}
		};
		let bestMatchKey = null;
		let maxTokens = -1;
		for (const key in filtersUrl) {
			if (key === "__catch-all__") continue;
			const requiredTokens = isEmpty(key) ? [] : key.split("|");
			if ((isEmpty(requiredTokens) ? true : requiredTokens.every((token) => token in f)) && requiredTokens.length > maxTokens) {
				maxTokens = requiredTokens.length;
				bestMatchKey = key;
			}
		}
		const activeFilterKeys = Object.keys(f);
		if (filtersUrl["__catch-all__"] && maxTokens < activeFilterKeys.length) {
			const allowedCatchAllFilters = filtersUrl["__catch-all__"].__filters__ ?? [];
			if (activeFilterKeys.some((key) => allowedCatchAllFilters.includes(key))) {
				const sortedActiveFilters = activeFilterKeys.filter((key) => allowedCatchAllFilters.includes(key)).sort((a, b) => allowedCatchAllFilters.indexOf(a) - allowedCatchAllFilters.indexOf(b));
				const urlKey = f[currentPageParam] ? "page" : "";
				if (filtersUrl["__catch-all__"][urlKey]) {
					let url = filtersUrl["__catch-all__"][urlKey].replace(":filters:", sortedActiveFilters.map((key) => stringifyFilterValue(key, f[key])).filter((el) => !isEmpty(el)).join("/"));
					url = trimEnd(url, "/");
					url = includes(url, "?") ? split(url, "?")[0] : url;
					const queryParams = new URLSearchParams(includes(url, "?") ? split(url, "?")[1] : "");
					for (const key in f) {
						const value = f[key];
						const token = `:${key}:`;
						if ([...allowedCatchAllFilters, currentPageParam].includes(key)) {
							if (url.includes(token)) url = url.replace(token, stringifyFilterValue(key, value));
							queryParams.delete(key);
						} else if (value !== "" && (!isArray(value) || value.length > 0)) {
							if (isArray(value)) for (const v of value) queryParams.set(`${key}[]`, String(v));
							else queryParams.set(key, String(value));
						}
					}
					const queryString = queryParams.toString();
					return queryString ? `${url}?${queryString}` : url;
				}
			}
		}
		if (bestMatchKey === null || !filtersUrl[bestMatchKey]) return null;
		let url = filtersUrl[bestMatchKey];
		url = includes(url, "?") ? split(url, "?")[0] : url;
		const queryParams = new URLSearchParams(includes(url, "?") ? split(url, "?")[1] : "");
		for (const key in f) {
			const value = f[key];
			const token = `:${key}:`;
			if (filtersUrl[bestMatchKey].includes(token)) {
				if (url.includes(token)) url = url.replace(token, stringifyFilterValue(key, value));
				queryParams.delete(key);
			} else if (value !== "" && (!isArray(value) || value.length > 0)) {
				if (isArray(value)) for (const v of value) queryParams.set(`${key}[]`, String(v));
				else queryParams.set(key, String(value));
			}
		}
		const queryString = queryParams.toString();
		return queryString ? `${url}?${queryString}` : url;
	}, [
		currentPageParam,
		filtersUrl,
		stringifyFilterValue
	]);
	const buildUrl = useCallback((forInertia, newFilters, page) => {
		if (!apiFetch || !fetchUrl) {
			const fUrl = getFrontendUrl(newFilters, page);
			const url = split(fUrl, "?")[0];
			const queryParams = new URLSearchParams(includes(fUrl, "?") ? split(fUrl, "?")[1] : "");
			if (forInertia) queryParams.set("inertia", "true");
			const queryString = queryParams.toString();
			return queryString ? `${url}?${queryString}` : url;
		}
		return buildApiPaginationUrl({
			fetchUrl,
			filters: newFilters,
			page,
			currentPageParam,
			perPage,
			perPageParam,
			stringifyFilterValue
		});
	}, [
		apiFetch,
		currentPageParam,
		fetchUrl,
		getFrontendUrl,
		perPage,
		perPageParam,
		stringifyFilterValue
	]);
	const [onApiResponse, setOnApiResponse] = useState(onApiResponseFromProps);
	const requestData = useCallback(async (url) => {
		try {
			const requestHeaders = {
				"X-Requested-With": "XMLHttpRequest",
				...apiFetch ? {} : { "X-Inertia": "true" }
			};
			const res = await axios.get(url, { headers: apiFetch ? Object.fromEntries(mergeRouteContextHeaders(url, requestHeaders).entries()) : requestHeaders });
			if (res.status === 200) {
				const { data: newData } = res;
				if (onApiResponse) await onApiResponse(newData);
				return {
					data: getDataFromApiResponse ? getDataFromApiResponse(newData) : ((d) => d.data)(newData),
					total: getTotalFromApiResponse ? getTotalFromApiResponse(newData) : ((d) => d.total)(newData)
				};
			}
		} catch (error) {}
		return {
			data: [],
			total: 0
		};
	}, [
		apiFetch,
		getDataFromApiResponse,
		getTotalFromApiResponse,
		mergeRouteContextHeaders,
		onApiResponse
	]);
	const fetchPage = useCallback(async (page) => requestData(buildUrl(!apiFetch, filters, page)), [
		apiFetch,
		buildUrl,
		filters,
		requestData
	]);
	const backfilling = useRef(false);
	useEffect(() => {
		const backfillMissingPages = async () => {
			const promises = Array.from({ length: currentPage - 1 }, (_, i) => i + 1).filter((page) => !data[page]).map((page) => new Promise((resolve) => {
				fetchPage(page).then((d) => {
					resolve({
						data: d.data,
						page
					});
				}).catch((e) => {
					console.error("Error on fetching page", page, e);
					resolve({
						data: [],
						page
					});
				});
			}));
			if (promises.length > 0) {
				const missingPagesData = await Promise.all(promises);
				setData((d) => ({
					...missingPagesData.reduce((acc, { page, data: newData }) => ({
						...acc,
						[page]: newData
					}), {}),
					...d
				}));
			}
		};
		if (infiniteScroll && currentPage > 1 && !backfilling.current) {
			backfilling.current = true;
			setLoading(true);
			backfillMissingPages().catch(() => {}).finally(() => {
				setLoading(false);
			});
		}
	}, [
		infiniteScroll,
		currentPage,
		fetchPage,
		data
	]);
	const [isAbove, setIsAbove] = useState(false);
	useEffect(() => {
		const checkPosition = () => {
			if (containerRef.current) {
				const rect = containerRef.current.getBoundingClientRect();
				setIsAbove(rect.top < 0);
			}
		};
		window.addEventListener("scroll", checkPosition);
		checkPosition();
		return () => {
			window.removeEventListener("scroll", checkPosition);
		};
	}, []);
	const { scrollingDownHeight, scrollingUpHeight } = useMenuManagerContext();
	const scrollToTopTimeout = useRef(null);
	const innerOnNavigate = useCallback(async (type) => {
		if (containerRef.current !== null && (!infiniteScroll || type !== "page-change")) {
			const offset = (isAbove ? scrollingUpHeight : scrollingDownHeight) + scrollTopOffset;
			const offsetPosition = containerRef.current.getBoundingClientRect().top + window.scrollY - offset;
			if (scrollToTopTimeout.current !== null) clearTimeout(scrollToTopTimeout.current);
			scrollToTopTimeout.current = setTimeout(() => {
				window.scrollTo({
					top: offsetPosition,
					behavior: "smooth"
				});
			}, 100);
		}
		if (onNavigate) await onNavigate(type);
	}, [
		infiniteScroll,
		isAbove,
		onNavigate,
		scrollTopOffset,
		scrollingDownHeight,
		scrollingUpHeight
	]);
	const goToPage = useCallback(async (page, force = false) => {
		const lastPage = Math.ceil(total / perPage);
		if (page < 1 || !force && page > lastPage || !force && page === innerCurrentPage) return;
		const url = buildUrl(false, filters, page);
		if (!force) await innerOnNavigate("page-change");
		if (apiFetch) {
			setLoading(true);
			if (!force) {
				const frontendUrl = getFrontendUrl(filters, page);
				if (frontendUrl) pushToHistory(frontendUrl);
			}
			const newData = await requestData(url);
			setData((d) => ({
				...d,
				[page]: newData.data
			}));
			setTotal(newData.total);
			setInnerCurrentPage(page);
			setLoading(false);
		} else if ((await navigateTo(url, reloadOnly, false)).type === "failed") setLoading(false);
	}, [
		total,
		perPage,
		innerCurrentPage,
		buildUrl,
		filters,
		innerOnNavigate,
		apiFetch,
		requestData,
		getFrontendUrl,
		pushToHistory,
		navigateTo,
		reloadOnly
	]);
	const nextPage = useCallback(async () => {
		if (innerCurrentPage <= Math.ceil(total / perPage) - 1) {
			if (infiniteScroll) {
				if (!loading) {
					await innerOnNavigate("page-change");
					setLoading(true);
					const nextUrl = buildUrl(false, filters, innerCurrentPage + 1);
					if (!apiFetch) window.history.replaceState(window.history.state, "", nextUrl);
					const newData = await fetchPage(innerCurrentPage + 1);
					setData((d) => ({
						...d,
						[innerCurrentPage + 1]: newData.data
					}));
					setTotal(newData.total);
					setInnerCurrentPage((p) => p + 1);
					setLoading(false);
					const frontendUrl = getFrontendUrl(filters, innerCurrentPage + 1);
					if (frontendUrl) window.history.replaceState(window.history.state, "", frontendUrl);
				}
			} else await goToPage(innerCurrentPage + 1);
		}
	}, [
		apiFetch,
		buildUrl,
		fetchPage,
		filters,
		getFrontendUrl,
		goToPage,
		infiniteScroll,
		innerCurrentPage,
		innerOnNavigate,
		loading,
		perPage,
		total
	]);
	const previousPage = useCallback(async () => {
		if (innerCurrentPage > 1) {
			if (infiniteScroll) {
				setLoading(true);
				const nextUrl = buildUrl(false, filters, innerCurrentPage - 1);
				if (!apiFetch) window.history.replaceState(window.history.state, "", nextUrl);
				setInnerCurrentPage((p) => p - 1);
				setLoading(false);
			} else await goToPage(innerCurrentPage - 1);
		}
	}, [
		apiFetch,
		buildUrl,
		filters,
		goToPage,
		infiniteScroll,
		innerCurrentPage
	]);
	const applyFilters = useCallback(async (newFilters) => {
		let updatedFilters;
		if (typeof newFilters === "function") updatedFilters = newFilters(filters);
		else updatedFilters = {
			...filters,
			...newFilters
		};
		updatedFilters = omitBy(updatedFilters, (v) => v === void 0 || v === "");
		if (filterSelectionLimits) {
			const clamped = { ...updatedFilters };
			for (const key in clamped) if (key in filterSelectionLimits) clamped[key] = clampFilterValue(clamped[key], filterSelectionLimits[key]);
			updatedFilters = clamped;
		}
		if (isEqual(updatedFilters, filters)) return;
		const url = buildUrl(false, updatedFilters, 1);
		await innerOnNavigate("filters-change");
		setLoading(true);
		if (apiFetch) {
			const frontendUrl = getFrontendUrl(updatedFilters, 1);
			if (frontendUrl) pushToHistory(frontendUrl);
			const newData = await requestData(url);
			setData({ 1: newData.data });
			setTotal(newData.total);
			setFilters(updatedFilters);
			setInnerCurrentPage(1);
		} else if ((await navigateTo(url, reloadOnly, false)).type === "error-page") return;
		setLoading(false);
	}, [
		filters,
		filterSelectionLimits,
		buildUrl,
		innerOnNavigate,
		apiFetch,
		getFrontendUrl,
		requestData,
		pushToHistory,
		navigateTo,
		reloadOnly
	]);
	const isFirstPage = useMemo(() => innerCurrentPage === 1, [innerCurrentPage]);
	const isLastPage = useMemo(() => innerCurrentPage * perPage >= total, [
		innerCurrentPage,
		perPage,
		total
	]);
	const currentData = useMemo(() => {
		if (infiniteScroll) return flatten(filter(sortBy(data, (_d, i) => i), (d) => d !== void 0));
		return data[innerCurrentPage] ?? [];
	}, [
		data,
		infiniteScroll,
		innerCurrentPage
	]);
	const noPrefetchDealt = useRef(false);
	useEffect(() => {
		if (!prefetched && !noPrefetchDealt.current) {
			noPrefetchDealt.current = true;
			goToPage(1, true).catch(() => {});
		}
	}, [goToPage, prefetched]);
	const totalPages = useMemo(() => Math.ceil(total / perPage), [total, perPage]);
	const goToFirstPage = useCallback(async () => {
		await goToPage(1);
	}, [goToPage]);
	const goToLastPage = useCallback(async () => {
		await goToPage(totalPages);
	}, [goToPage, totalPages]);
	const containerRef = useRef(null);
	const onInfiniteScrollEndReached = useCallback(async () => {
		await nextPage();
	}, [nextPage]);
	useInfiniteScroll({
		active: !loading && infiniteScroll,
		containerRef,
		offset: 300,
		onEndReached: onInfiniteScrollEndReached
	});
	const setContainerRef = useCallback((ref) => {
		containerRef.current = ref;
	}, []);
	const value = useMemo(() => ({
		data: currentData,
		loading,
		setContainerRef,
		currentPage: innerCurrentPage,
		totalPages,
		isFirstPage,
		isLastPage,
		goToPage,
		goToFirstPage,
		goToLastPage,
		previousPage,
		nextPage,
		applyFilters,
		getUrl: getFrontendUrl,
		currentFilters: filters,
		infiniteScroll,
		totalItems: total,
		onApiResponse: setOnApiResponse
	}), [
		applyFilters,
		currentData,
		filters,
		goToFirstPage,
		goToLastPage,
		goToPage,
		infiniteScroll,
		innerCurrentPage,
		isFirstPage,
		isLastPage,
		loading,
		nextPage,
		getFrontendUrl,
		previousPage,
		setContainerRef,
		total,
		totalPages
	]);
	return /* @__PURE__ */ jsx(PaginationContext.Provider, {
		value,
		children
	});
}
function usePagination() {
	const context = useContext(PaginationContext);
	if (context === void 0) throw new Error("usePagination must be used within a PaginationProvider");
	return context;
}
//#endregion
export { PaginationProvider, usePagination };
